<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { getTranslation } from '@/utils/data-display-utils';

import TimelineItem from '../TimelineItem.vue';
import TimelineItemModal from '../TimelineItemModal.vue';

import { timelineStepsService } from '@/services/timelineSteps.service';
import { Module } from '@/store/module.interface';
import { useCommunityStore } from '@/store/communityStore';
import { Project } from '@/store/project.interface';
import { constant } from '@/constant';
import { DTOTimelineStep } from '@/store/timelineStep.interface';
import { Locales } from '@/store/language.interface';

// STORE
const communityStore = useCommunityStore();

// CONST
const timelineSteps = ref<DTOTimelineStep[]>([]);
const timelineitemDialogRef = ref(null);
const openedItem = ref<DTOTimelineStep | undefined>(undefined);
const swiper = ref();

// PROPS
const props = defineProps<{
  module: Module;
  languageEnabled?: Locales[];
}>();

// LIFECYCLE
onMounted(async () => {
  timelineSteps.value = await timelineStepsService.getModuleTimelineSteps(project.value.id, props.module.id);
  const step =
    timelineSteps.value.find((item) => item.status === constant.ProjectModule.TimelineStep.Status.Current)?.id ||
    timelineSteps.value[0]?.id ||
    null;
  swiper.value.breakpoints = setBreakpointsConfig();
  swiper.value.initialSlide = timelineSteps.value.findIndex((item) => item.id === step);
  swiper.value.initialize();
});

// COMPUTED
const project = computed<Project>(() => {
  return communityStore.community.projects.find((project) => project.id == props.module.project_id);
});

// FUNCTIONS
function openTimelineItemModal(id: number) {
  openedItem.value = timelineSteps.value.find((item) => item.id === id);
  timelineitemDialogRef.value.openDialog();
}

function setBreakpointsConfig() {
  const totalSteps = timelineSteps.value.length;

  return {
    400: {
      slidesPerView: 1,
    },
    550: {
      slidesPerView: totalSteps >= 2 ? 2 : 1,
    },
    800: {
      slidesPerView: totalSteps >= 3 ? 3 : totalSteps,
    },
  };
}
</script>
<template>
  <div class="module-timeline">
    <div class="module-timeline__module-title">
      <h2>{{ getTranslation(props.module, 'title', languageEnabled) }}</h2>
    </div>
    <div class="swiper-container">
      <q-btn flat round dense text-color="primary" icon="chevron_left" id="swiper-prev" />
      <swiper-container
        ref="swiper"
        space-between="20"
        navigation-prev-el="#swiper-prev"
        navigation-next-el="#swiper-next"
        centered-slides="false"
        init="false"
      >
        <swiper-slide v-for="item in timelineSteps">
          <timeline-item
            :item="item"
            :language-enabled="props.languageEnabled"
            is-featured-item
            @see-more="openTimelineItemModal(item.id)"
          />
        </swiper-slide>
      </swiper-container>
      <q-btn flat round dense text-color="primary" icon="chevron_right" id="swiper-next" />
    </div>
    <timeline-item-modal :item="openedItem" :language-enabled="props.languageEnabled" ref="timelineitemDialogRef" />
  </div>
</template>
<style lang="scss">
.module-timeline {
  display: flex;
  flex-direction: column;
  gap: $space-sm;

  .swiper-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 350px;

    swiper-container {
      width: 100%;
      overflow: hidden;
      height: inherit;
      padding: $space-sm 0 0 0;

      swiper-slide {
        height: inherit;
        display: flex;
        justify-content: center;
      }
    }
  }
}
</style>
